// Firebase app initialization. Web config comes from Vite env vars so no
// keys are committed; Firebase web config is not an authorization secret.
import { initializeApp } from "firebase/app";
import type { FirebaseOptions } from "firebase/app";
if (
  typeof crypto !== "undefined" &&
  typeof crypto.randomUUID !== "function" &&
  typeof crypto.getRandomValues === "function"
) {
  Object.defineProperty(crypto, "randomUUID", {
    value: () => {
      const b = new Uint8Array(16);
      crypto.getRandomValues(b);
      b[6] = (b[6]! & 0x0f) | 0x40;
      b[8] = (b[8]! & 0x3f) | 0x80;
      const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
      return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20, 32)}`;
    },
    configurable: true,
  });
}
import { getAuth } from "firebase/auth";
import {
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
} from "firebase/firestore";
import { getDatabase } from "firebase/database";
import { getFunctions } from "firebase/functions";
import { connectEmulators } from "./emulators";

// Auth popups must be same-origin with the app: when served from a custom
// domain, using the default <project>.firebaseapp.com authDomain makes the
// popup third-party, and browsers that partition cross-site storage wipe its
// sessionStorage ("missing initial state" error). Every Hosting-connected
// domain serves /__/auth/handler, so prefer the current hostname.
const envAuthDomain =
  import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ||
  "demo-brandoball.firebaseapp.com";
// window/indexedDB don't exist under Node (emulator tests) — fall back safely.
const hostname =
  typeof window !== "undefined" ? window.location.hostname : "localhost";
const isDefaultHost =
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname.endsWith(".web.app") ||
  hostname.endsWith(".firebaseapp.com");
const authDomain = isDefaultHost ? envAuthDomain : hostname;

const firebaseConfig: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "demo-api-key",
  authDomain,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "demo-brandoball",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "demo-app-id",
  databaseURL:
    import.meta.env.VITE_FIREBASE_DATABASE_URL ||
    "https://demo-brandoball-default-rtdb.firebaseio.com",
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
// Persistent cache: reloads render from IndexedDB instantly while snapshots
// revalidate against the server. Multi-tab manager keeps tabs consistent.
// Falls back to the default in-memory cache where IndexedDB is unavailable.
export const db = initializeFirestore(app, {
  localCache:
    typeof indexedDB !== "undefined"
      ? persistentLocalCache({ tabManager: persistentMultipleTabManager() })
      : memoryLocalCache(),
});
export const rtdb = getDatabase(app);
export const functions = getFunctions(app);

connectEmulators({ auth, db, rtdb, functions });
